import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../../lib/api";
import { OPERATOR_NAME_MAX, inviteFormIssues } from "../../lib/operator";
import type { InvitedOperator } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { MicroLabel } from "../ui/Text";
import { AccessPicker } from "./AccessPicker";

/**
 * Inviting a colleague to the panel.
 *
 * **No password field.** The account is created `invited`, with no password at all,
 * and the server hands back a one-time link for its owner to choose one. An admin
 * who typed a colleague's first password would know it until it was changed — and
 * nothing would make them change it.
 *
 * Starts with nothing ticked. There is no role every deployment has whose name
 * means "the narrow one", so any default would be a guess about access — and an
 * account that can do too much is the mistake nobody notices. The submit stays
 * disabled until a role or a permission is chosen.
 */
export function InviteOperator({
  onInvited,
  onCancel,
}: {
  readonly onInvited: (result: InvitedOperator) => void;
  readonly onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roleIds, setRoleIds] = useState<readonly string[]>([]);
  const [permissions, setPermissions] = useState<readonly string[]>([]);

  const invite = useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      roleIds: readonly string[];
      permissions: readonly string[];
    }) => post<InvitedOperator>("/operators", body),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
      onInvited(result);
    },
  });

  const issues = inviteFormIssues({ email, name, roleIds, permissions });
  const canSubmit =
    email.trim() !== "" &&
    name.trim() !== "" &&
    Object.keys(issues).length === 0 &&
    !invite.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>Invite an operator</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {/* A 409 here is "that address already has an account" — the server's own
          sentence says so, and it is the one an admin needs. */}
      {invite.isError ? <ErrorNotice error={invite.error} /> : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          invite.mutate({ email: email.trim(), name: name.trim(), roleIds, permissions });
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2"
      >
        <div className="flex flex-col gap-5">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
            autoComplete="off"
            required
            autoFocus
            hint="What they will sign in with. It cannot be changed later."
            {...(issues.email === undefined ? {} : { error: issues.email })}
          />
          <Field
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={OPERATOR_NAME_MAX}
            autoComplete="off"
            required
            hint="Shown in the audit log beside everything they do."
            {...(issues.name === undefined ? {} : { error: issues.name })}
          />
        </div>

        {/* Full width: the direct-permission grid needs the room. */}
        <div className="md:col-span-2">
          <AccessPicker
            roleIds={roleIds}
            permissions={permissions}
            onRolesChange={setRoleIds}
            onPermissionsChange={setPermissions}
            error={issues.access}
          />
        </div>

        <div className="flex items-end md:col-span-2">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={invite.isPending}>
            Create invitation
          </Button>
        </div>
      </form>
    </div>
  );
}
